import React, { useRef } from 'react';
import { Box, Typography, Card, IconButton } from '@mui/material';
import { ChevronLeft, ChevronRight, Star, StarBorder } from '@mui/icons-material';
import { guestReviews } from '../config/info';

const CARD_W = 320;
const GAP = 16;

function StarRating({ rating }) {
  const value = Math.min(5, Math.max(0, Math.round(Number(rating) || 0)));
  return (
    <Box sx={{ display: 'flex', gap: 0.25 }} aria-label={`${value} out of 5 stars`}>
      {[1, 2, 3, 4, 5].map((n) =>
        n <= value ? (
          <Star key={n} sx={{ fontSize: 20, color: '#c9a227' }} />
        ) : (
          <StarBorder key={n} sx={{ fontSize: 20, color: 'rgba(44,41,38,0.25)' }} />
        )
      )}
    </Box>
  );
}

function ReviewCard({ review, inCarousel }) {
  return (
    <Card
      elevation={0}
      sx={{
        borderRadius: 2,
        border: '1px solid var(--color-sand)',
        bgcolor: 'white',
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        ...(inCarousel
          ? {
              flex: `0 0 ${CARD_W}px`,
              width: CARD_W,
              scrollSnapAlign: 'start',
            }
          : {
              maxWidth: 520,
              width: '100%',
            }),
      }}
    >
      <Box sx={{ p: 2.5, flex: 1, display: 'flex', flexDirection: 'column', gap: 1.5 }}>
        <Typography
          sx={{
            fontSize: '2.5rem',
            lineHeight: 1,
            color: 'var(--color-accent)',
            opacity: 0.35,
            fontFamily: "'Cormorant Garamond', Georgia, serif",
            userSelect: 'none',
          }}
          aria-hidden
        >
          &ldquo;
        </Typography>
        <Typography
          sx={{
            color: 'var(--color-charcoal)',
            fontSize: '0.95rem',
            lineHeight: 1.7,
            flex: 1,
          }}
        >
          {review.review}
        </Typography>
        <Box sx={{ pt: 1, borderTop: '1px solid rgba(44,41,38,0.08)' }}>
          <StarRating rating={review.rating} />
          <Typography
            component="p"
            sx={{
              mt: 1,
              fontWeight: 600,
              fontSize: '0.9rem',
              color: 'var(--color-charcoal)',
            }}
          >
            {review.customerName}
          </Typography>
        </Box>
      </Box>
    </Card>
  );
}

function ReviewsCarousel({ reviews }) {
  const scrollerRef = useRef(null);
  const scrollBy = (deltaCards) => {
    const el = scrollerRef.current;
    if (!el) return;
    el.scrollBy({ left: deltaCards * (CARD_W + GAP), behavior: 'smooth' });
  };

  const list = Array.isArray(reviews) ? reviews : [];
  if (list.length === 0) return null;

  return (
    <Box>
      {list.length > 1 && (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'flex-end',
            gap: 1,
            mb: 1.5,
          }}
        >
          <IconButton
            onClick={() => scrollBy(-1)}
            aria-label="Scroll reviews left"
            size="small"
            sx={{
              border: '1px solid var(--color-sand)',
              bgcolor: 'rgba(0,0,0,0.02)',
              '&:hover': { bgcolor: 'rgba(0,0,0,0.04)' },
            }}
          >
            <ChevronLeft />
          </IconButton>
          <IconButton
            onClick={() => scrollBy(1)}
            aria-label="Scroll reviews right"
            size="small"
            sx={{
              border: '1px solid var(--color-sand)',
              bgcolor: 'rgba(0,0,0,0.02)',
              '&:hover': { bgcolor: 'rgba(0,0,0,0.04)' },
            }}
          >
            <ChevronRight />
          </IconButton>
        </Box>
      )}

      <Box
        ref={scrollerRef}
        sx={{
          display: 'flex',
          gap: `${GAP}px`,
          overflowX: 'auto',
          scrollSnapType: 'x mandatory',
          WebkitOverflowScrolling: 'touch',
          pb: 1,
          mx: -1,
          px: 1,
          alignItems: 'stretch',
          '&::-webkit-scrollbar': { height: 8 },
          '&::-webkit-scrollbar-thumb': { background: 'rgba(44,41,38,0.18)', borderRadius: 10 },
          '&::-webkit-scrollbar-track': { background: 'rgba(44,41,38,0.06)', borderRadius: 10 },
        }}
      >
        {list.map((item, i) => (
          <ReviewCard key={`${item.customerName}-${i}`} review={item} inCarousel />
        ))}
      </Box>
    </Box>
  );
}

const ReviewsSection = () => {
  const reviews = Array.isArray(guestReviews) ? guestReviews : [];
  if (reviews.length === 0) return null;

  return (
    <Box
      id="reviews"
      component="section"
      aria-labelledby="reviews-heading"
      sx={{
        py: { xs: 7, md: 9 },
        px: 3,
        bgcolor: 'var(--color-cream)',
        borderTop: '1px solid rgba(44,41,38,0.06)',
        borderBottom: '1px solid rgba(44,41,38,0.06)',
      }}
    >
      <Box sx={{ maxWidth: 1200, mx: 'auto' }}>
        <Typography
          variant="overline"
          sx={{ letterSpacing: 3, color: 'var(--color-warm-gray)', fontWeight: 500, fontSize: '0.85rem' }}
        >
          Guest experiences
        </Typography>
        <Typography
          id="reviews-heading"
          component="h2"
          sx={{
            fontFamily: "'Cormorant Garamond', Georgia, serif",
            fontSize: { xs: '2rem', md: '2.5rem' },
            fontWeight: 500,
            mt: 1,
            mb: 1,
            color: 'var(--color-charcoal)',
          }}
        >
          What our guests say
        </Typography>
        <Typography
          sx={{
            color: 'var(--color-warm-gray)',
            fontSize: '1rem',
            lineHeight: 1.7,
            mb: 3,
            maxWidth: 640,
          }}
        >
          Real stays at Chan&apos;s Nest Inn — peaceful rooms near Sholayar Dam and Valparai.
        </Typography>

        <ReviewsCarousel reviews={reviews} />
      </Box>
    </Box>
  );
};

export default ReviewsSection;
